/* Feb-2026 · Finance AI Advisor sidebar.
 *
 * A persistent right-column consultant that lives next to the
 * Financial Statements panel. Pulls the current period snapshot from
 * the backend, sends it through Claude Sonnet 4.6 (via Emergent
 * Universal Key), and renders three actionable recommendation cards:
 *
 *   1. Cash Flow  — runway, burn, receivables timing
 *   2. Churn      — retention save-plays, downgrade cohorts
 *   3. Pricing    — elasticity, tier packing, promo timing
 *
 * Every refresh is written to `admin_financial_recommendations` so
 * the operator can browse prior consultations from the audit trail.
 */
import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, RefreshCw, Loader2, ArrowRightLeft, TrendingDown,
  DollarSign, ChevronDown, ChevronRight,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const CARDS = [
  { key: 'cash_flow', label: 'Cash Flow',  icon: ArrowRightLeft, tint: 'text-primary',    unit: 'usd' },
  { key: 'churn',     label: 'Churn',      icon: TrendingDown,   tint: 'text-warning',    unit: 'pct' },
  { key: 'pricing',   label: 'Pricing',    icon: DollarSign,     tint: 'text-success',    unit: 'usd' },
];

function fmtImpact(rec, unit) {
  if (!rec) return '—';
  if (unit === 'pct') {
    const v = rec.expected_impact_pct;
    return v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
  }
  const v = rec.expected_impact_usd;
  if (v == null) return '—';
  const sign = v < 0 ? '−' : '+';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function ConfidencePill({ level }) {
  const tone = level === 'high'   ? 'bg-success/20 text-success border-success/40' :
                level === 'medium' ? 'bg-warning/20 text-warning border-warning/40' :
                                     'bg-muted text-muted-foreground border-border';
  return (
    <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[9.5px] font-mono-stat uppercase tracking-wider ${tone}`}>
      {level || 'unknown'}
    </span>
  );
}

function AdviceCard({ spec, rec, expanded, onToggle }) {
  if (!rec) {
    return (
      <div className="rounded-md border border-border bg-card p-3 text-[11.5px] text-muted-foreground" data-testid={`fin-advice-card-${spec.key}-empty`}>
        <spec.icon className={`inline h-3.5 w-3.5 ${spec.tint}`} /> {spec.label} — advice pending.
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-card shadow-btn transition-shadow hover:shadow-btn-hover" data-testid={`fin-advice-card-${spec.key}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left"
        data-testid={`fin-advice-card-${spec.key}-toggle`}
      >
        <spec.icon className={`mt-0.5 h-4 w-4 shrink-0 ${spec.tint}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{spec.label}</span>
            <ConfidencePill level={rec.confidence} />
            <span className={`ml-auto font-mono-stat text-[12px] font-semibold ${spec.tint}`}>
              {fmtImpact(rec, spec.unit)}
            </span>
          </div>
          <div className="mt-1 text-[13px] font-semibold leading-snug">{rec.headline || '—'}</div>
          <div className="mt-1 text-[12px] text-foreground/85 leading-snug">{rec.action || ''}</div>
        </div>
        {expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
      {expanded && rec.reasoning && (
        <div className="border-t border-border bg-panel-header/30 px-3 py-2 text-[11.5px] leading-relaxed text-foreground/85" data-testid={`fin-advice-card-${spec.key}-reasoning`}>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Why:</span>{' '}
          {rec.reasoning}
        </div>
      )}
    </div>
  );
}

export default function FinanceAdvisorSidebar({ period }) {
  const [advice, setAdvice] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [focus, setFocus]     = useState('');
  const [generatedAt, setAt]  = useState(null);
  const [expanded, setExpanded] = useState({});

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.post('/admin/financials/ai-advice', { period, focus: focus.trim() || undefined });
      setAdvice(r.data?.advice || null);
      setAt(r.data?.generated_at || null);
      toast.success('Financial advisor refreshed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Advisor could not respond.');
    } finally { setBusy(false); }
  }, [period, focus]);

  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col rounded-md border border-border bg-panel-header/20"
      data-testid="fin-advisor-sidebar"
    >
      <header className="flex items-center gap-2 border-b border-border bg-panel-header/60 px-3 py-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold leading-tight">AI Consultant</div>
          <div className="truncate text-[10.5px] text-muted-foreground">
            Claude Sonnet 4.6 · period <span className="font-mono-stat">{period}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          data-testid="fin-advisor-refresh"
          className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground shadow-btn hover:shadow-btn-hover disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {busy ? 'Consulting…' : 'Get advice'}
        </button>
      </header>

      <div className="border-b border-border bg-card/40 px-3 py-2">
        <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Focus (optional)</label>
        <input
          type="text"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') refresh(); }}
          placeholder="e.g. runway anxiety · Premier churn · promo timing"
          className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
          data-testid="fin-advisor-focus"
        />
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto scroll-thin p-3">
        {!advice && !busy && (
          <div className="rounded-md border border-dashed border-border/60 p-4 text-center text-[12px] text-muted-foreground" data-testid="fin-advisor-empty">
            No advice generated yet. Click <b>Get advice</b> and the consultant will review the current period's statements to recommend three moves for cash flow, churn, and pricing.
          </div>
        )}
        {advice?.raw && (
          <pre className="whitespace-pre-wrap rounded-md border border-warning/40 bg-warning/10 p-3 text-[11px] text-warning-foreground" data-testid="fin-advisor-raw">
            The consultant returned unstructured advice this run:{'\n\n'}{advice.raw}
          </pre>
        )}
        {advice && !advice.raw && CARDS.map((c) => (
          <AdviceCard
            key={c.key}
            spec={c}
            rec={advice[c.key]}
            expanded={!!expanded[c.key]}
            onToggle={() => setExpanded((prev) => ({ ...prev, [c.key]: !prev[c.key] }))}
          />
        ))}
      </div>

      {generatedAt && (
        <div className="shrink-0 border-t border-border bg-panel-header/40 px-3 py-1.5 text-[10.5px] text-muted-foreground" data-testid="fin-advisor-generated-at">
          Last refreshed {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </aside>
  );
}
